import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Stack, Typography } from '@mui/material';

import {
	MplButton,
	MplInputV2,
	StyledDTicketPageLayoutFooter,
} from '@mopla/ui';

import { useManageBillingAddressForm } from './useManageBillingAddressForm';

export const ManageBillingAddressForm = () => {
	const { t } = useTranslation('manageBillingAddress');

	const {
		control,
		errors,
		isDisabled,
		requestError,
		handleSubmit,
		saveChanges,
	} = useManageBillingAddressForm();

	return (
		<>
			{/* pb clears the fixed footer (Save button) so the last fields (post code / city)
			    can scroll fully into view above the native keyboard on mobile */}
			<Stack gap={1.5} sx={{ pb: '120px' }}>
				<Typography variant="h3-sb" color="primary.main">
					{t('section_title')}
				</Typography>

				<Stack gap={2}>
					<Controller
						name="firstName"
						control={control}
						render={({ field }) => (
							<MplInputV2
								{...field}
								ref={null}
								label={t('first_name_label')}
								data-testid="billing-address-first-name-input"
								helperText={
									Boolean(errors.firstName?.message) &&
									t(errors.firstName?.message as string)
								}
								error={Boolean(errors.firstName?.message)}
							/>
						)}
					/>

					<Controller
						name="lastName"
						control={control}
						render={({ field }) => (
							<MplInputV2
								{...field}
								ref={null}
								label={t('last_name_label')}
								data-testid="billing-address-last-name-input"
								helperText={
									Boolean(errors.lastName?.message) &&
									t(errors.lastName?.message as string)
								}
								error={Boolean(errors.lastName?.message)}
							/>
						)}
					/>

					<Controller
						name="company"
						control={control}
						render={({ field }) => (
							<MplInputV2
								{...field}
								ref={null}
								label={t('company_label')}
								data-testid="billing-address-company-input"
								helperText={
									Boolean(errors.company?.message) &&
									t(errors.company?.message as string)
								}
								error={Boolean(errors.company?.message)}
							/>
						)}
					/>

					<Stack direction="row" gap={1}>
						<Stack sx={{ flex: 1 }}>
							<Controller
								name="street"
								control={control}
								render={({ field }) => (
									<MplInputV2
										{...field}
										ref={null}
										label={t('street_label')}
										data-testid="billing-address-street-input"
										helperText={
											Boolean(errors.street?.message) &&
											t(errors.street?.message as string)
										}
										error={Boolean(errors.street?.message)}
									/>
								)}
							/>
						</Stack>
						<Stack sx={{ width: 96, flexShrink: 0 }}>
							<Controller
								name="streetNumber"
								control={control}
								render={({ field }) => (
									<MplInputV2
										{...field}
										ref={null}
										label={t('street_number_label')}
										data-testid="billing-address-street-number-input"
										helperText={
											Boolean(errors.streetNumber?.message) &&
											t(errors.streetNumber?.message as string)
										}
										error={Boolean(errors.streetNumber?.message)}
									/>
								)}
							/>
						</Stack>
					</Stack>

					<Stack direction="row" gap={1}>
						<Stack sx={{ width: 96, flexShrink: 0 }}>
							<Controller
								name="postCode"
								control={control}
								render={({ field }) => (
									<MplInputV2
										{...field}
										ref={null}
										label={t('post_code_label')}
										data-testid="billing-address-post-code-input"
										helperText={
											Boolean(errors.postCode?.message) &&
											t(errors.postCode?.message as string)
										}
										error={Boolean(errors.postCode?.message)}
									/>
								)}
							/>
						</Stack>
						<Stack sx={{ flex: 1 }}>
							<Controller
								name="location"
								control={control}
								render={({ field }) => (
									<MplInputV2
										{...field}
										ref={null}
										label={t('location_label')}
										data-testid="billing-address-location-input"
										helperText={
											Boolean(errors.location?.message) &&
											t(errors.location?.message as string)
										}
										error={Boolean(errors.location?.message)}
									/>
								)}
							/>
						</Stack>
					</Stack>
				</Stack>

				{requestError && (
					<Typography variant="b2" color="error.main">
						{requestError}
					</Typography>
				)}
			</Stack>
			<StyledDTicketPageLayoutFooter>
				<MplButton
					variant="contained"
					color="primary"
					fullWidth
					disabled={isDisabled}
					onClick={handleSubmit(saveChanges)}
					testId="billing-address-save-button"
				>
					{t('save_button')}
				</MplButton>
			</StyledDTicketPageLayoutFooter>
		</>
	);
};
